import {
  Accordion,
  Button,
  CardItemBreedQuizResult,
  CardItemContainer,
  DetailList,
  EmptyOverlay,
  HeaderProfile,
  LeadForm,
  SectionText,
  SectionTitle,
  SideLayoutContentWrapper,
  SideLayoutSideWrapper,
  SideLayoutStickyWrapper,
  SideLayoutWrapper,
} from '@presentation/components';
import { BREAKPOINTS } from '@presentation/styles';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { TbBellRinging } from 'react-icons/tb';
import { useMediaQuery } from 'react-responsive';
import { ClassNames } from '@emotion/react';
import { Tooltip } from 'react-tooltip';
import { PageSkeleton } from './components/PageSkeleton';
import * as Styled from './styled';
import type { QuizResultsPageProps } from './types';

export * from './types';

export const QUIZ_RESULTS_TOOLTIP_ID = 'quiz-subscribe-tooltip';

export const QuizResultsPage: FC<QuizResultsPageProps> = (props) => {
  const {
    answers,
    loading = false,
    items = [],
    showNewsletterForm = false,
    newsletterForm,
    onRestartClick,
    onSubscribeClick,
  } = props;
  const { translate } = useTranslation();
  const isTablet = useMediaQuery({ query: `(max-width: ${BREAKPOINTS.tablet}px)` });
  const isMobile = useMediaQuery({ query: `(max-width: ${BREAKPOINTS.phone}px)` });

  if (loading) return <PageSkeleton />;

  return (
    <>
      <HeaderProfile
        title={translate('quiz.result.title')}
        subtitle={translate('quiz.result.subtitle')}
      />
      {!!showNewsletterForm && (
        <LeadForm {...newsletterForm} hideHasADogField hideLookingForField hideSensitivityField />
      )}
      {!showNewsletterForm && (
        <Styled.QuizResultContainer>
          <SideLayoutWrapper>
            <Tooltip id={QUIZ_RESULTS_TOOLTIP_ID} style={{ zIndex: 99 }} />

            <SideLayoutContentWrapper>
              {!items || !items.length ? (
                <EmptyOverlay title={translate('quiz.result.noResult')} />
              ) : (
                <>
                  {isTablet && (
                    <Styled.HowToSubscribeHelper>
                      <TbBellRinging />
                      {translate(
                        'common.sections.breedInterestSubscription.subscribeToBreedHelperText',
                      )}
                    </Styled.HowToSubscribeHelper>
                  )}

                  <CardItemContainer>
                    {items.map((item, index) => (
                      <Styled.CardItemWrapper key={index}>
                        <CardItemBreedQuizResult
                          {...item}
                          onSubscribeClick={() => {
                            onSubscribeClick(item.name as string);
                          }}
                        />
                      </Styled.CardItemWrapper>
                    ))}
                  </CardItemContainer>
                </>
              )}
            </SideLayoutContentWrapper>

            <SideLayoutSideWrapper>
              <SideLayoutStickyWrapper marginBottom={30}>
                <Styled.SideContentWrapper>
                  <SectionTitle>{translate('quiz.result.summaryTitle')}</SectionTitle>
                  <SectionText>{translate('quiz.result.summarySubtitle')}</SectionText>
                  <ClassNames>
                    {({ css: classNameCss }) => {
                      const wrapperClassname = classNameCss(Styled.accordionWrapperStyle());
                      return (
                        <Accordion
                          wrapperClass={wrapperClassname}
                          tabs={[
                            {
                              title: translate('quiz.result.summaryTitle'),
                              content: (
                                <DetailList
                                  items={Object.values(answers).map((answerKey, index) => ({
                                    label: translate(`quiz.steps.step${index + 1}.result`),
                                    value: translate(
                                      `quiz.steps.step${index + 1}.answers.${answerKey}`,
                                      {
                                        defaultValue: 'N/A',
                                      },
                                    ),
                                  }))}
                                />
                              ),
                              defaultChecked: !isMobile,
                            },
                          ]}
                        />
                      );
                    }}
                  </ClassNames>
                  <Button color="blue" outlined onClick={onRestartClick}>
                    {translate('common.buttons.redoTest')}
                  </Button>
                </Styled.SideContentWrapper>
              </SideLayoutStickyWrapper>
            </SideLayoutSideWrapper>
          </SideLayoutWrapper>
        </Styled.QuizResultContainer>
      )}
    </>
  );
};
